import { TimeDuration } from 'common/components/WizardControls/WizardDurationInput';
import {
  Contractor,
  ContractorPickAssignment,
  ContractorRecord,
} from 'common/models';
import { TimeWithoutDate } from 'common/models/dateType';
import { HaulerPickRecord } from 'common/models/harvestData/hauler';
import { PartialPickRecord } from 'common/models/harvestData/partialPickRecord';
import { HarvestData } from '../components/PickDataWizard/HarvestDataForm';
import { HaulData } from '../components/PickDataWizard/HaulDataForm';

const formatToTimeDuration = (time: TimeWithoutDate): TimeDuration => {
  const timeArr = time.split(':');
  return {
    hours: parseInt(timeArr[0], 10),
    minutes: parseInt(timeArr[1], 10),
  };
};

export const formatFromTimeDuration = (time: TimeDuration): TimeWithoutDate =>
  `${time.hours || '00'}:${time.minutes || '00'}:00`;

export const createDefaultHarvestRecords = (
  assignments: ContractorPickAssignment[],
  partialRecords: PartialPickRecord[],
  defaultPickRate: string | undefined,
): Omit<HarvestData, 'id'>[] =>
  partialRecords.length === 0
    ? assignments.map(({ contractor }) => ({
        startTime: null,
        contractor: {
          label: contractor.code,
          value: contractor,
        },
        headCount: null,
        timeWorked: { hours: 0, minutes: 0 },
        transferTime: { hours: 0, minutes: 0 },
        binsPicked: null,
        binsHauled: null,
        pickRate: defaultPickRate || null,
        haulRate: null,
        tarps: null,
      }))
    : partialRecords.map(
        ({
          contractor,
          startTime,
          headCount,
          hoursWorked,
          transferTime,
          pickRate,
        }) => ({
          contractor: {
            label: contractor.code,
            value: contractor,
          },
          startTime: startTime ? new Date(startTime) : null,
          headCount: headCount?.toString() || null,
          timeWorked:
            hoursWorked && hoursWorked !== '00:00:00'
              ? formatToTimeDuration(hoursWorked)
              : { hours: 0, minutes: 0 },
          transferTime: transferTime
            ? formatToTimeDuration(transferTime)
            : { hours: 0, minutes: 0 },
          binsPicked: null,
          binsHauled: null,
          pickRate: pickRate || defaultPickRate || null,
          haulRate: null,
          tarps: null,
        }),
      );

export const createHarvestRecords = (
  contractorRecords: ContractorRecord[],
): HarvestData[] =>
  contractorRecords.map(record => ({
    id: record.id.toString(),
    startTime: new Date(record.startTime),
    contractor: {
      label: record.contractor.code,
      value: record.contractor,
    },
    headCount: record.headCount.toString(),
    timeWorked: formatToTimeDuration(record.hoursWorked),
    transferTime: formatToTimeDuration(record.transferTime),
    pickRate: record.pickRate.toString(),
    binsPicked: record.binsPicked.toString(),
    binsHauled: record.binsHauled ? record.binsHauled.toString() : null,
    tarps: record.tarps ? record.tarps.toString() : null,
    haulRate: record.haulRate
      ? {
          label: `${record.haulRate.haulLabel.miles} mi`,
          value: record.haulRate,
        }
      : null,
  }));

export const createDefaultHaulRecords = (
  haulers: Contractor[] | undefined,
): Omit<HaulData, 'id'>[] =>
  haulers?.map(record => ({
    binsHauled: null,
    tarps: null,
    haulRate: null,
    hauler: {
      label: record.code,
      value: record,
    },
  })) || [];

export const createHaulRecords = (
  haulerRecords: HaulerPickRecord[],
): HaulData[] =>
  haulerRecords.map(record => ({
    id: record.id.toString(),
    binsHauled: record.binsHauled.toString(),
    tarps: record.tarps.toString(),
    haulRate: {
      label: `${record.haulRate.haulLabel.miles} mi`,
      value: record.haulRate,
    },
    hauler: {
      label: record.hauler.code,
      value: record.hauler,
    },
  }));
